import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function EarningsAndExpenses ({
  earnings,
  expenses,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [earningsAndExpenses, setEarningsAndExpenses] = useState ([])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `?business_id=${businessId}&start_date=${startDate}&end_date=${endDate}`
        
      const { data: earnings } = await cutileiApi.get (`/earnings${filters}`, requestConfig)
      const { data: expenses } = await cutileiApi.get (`/expenses${filters}`, requestConfig)

      const earningsByMonth = Dashboard.groupEarningsByMonth (earnings.map (earning => ({
        ...earning, date: DateTime.fromISO (earning.cash_register.created_at).toISODate ()
      })).sort (orderEarningsAndExpenses))

      const expensesByMonth = Dashboard.groupEarningsByMonth (expenses.map (expense => ({
        ...expense, date: DateTime.fromISO (expense.cash_register.created_at).toISODate ()
      })).sort (orderEarningsAndExpenses))

      const months = [...new Set (Object.keys ({...earningsByMonth, ...expensesByMonth}))]
      
      setEarningsAndExpenses ([
        ['Mês / ano', 'Suprimentos', 'Sangrias'],
        ...months.map (month => ([
          month,
          earningsByMonth[month]?.reduce ((total, earning) => total + earning.value, 0),
          expensesByMonth[month]?.reduce ((total, expense) => total + expense.value, 0)
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const orderEarningsAndExpenses = (a, b) => (
    DateTime.fromISO (a.date).toMillis () - DateTime.fromISO (b.date).toMillis ()
  )

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!earnings && !expenses || earnings.length === 0 && expenses.length === 0) {
    return null
  }

  const monthEarningsAndExpenses = [
    ['Origem', 'Valor (R$)'],
    ['Suprimentos', earnings?.reduce ((total, earning) => total + earning.value, 0)],
    ['Sangrias', expenses?.reduce ((total, expense) => total + expense.value, 0)]
  ]

  return (
    <InfoContainer>
      <SubTitle>Suprimentos e sangrias</SubTitle>
      <PieChart
        data={monthEarningsAndExpenses}
        options={{colors: ['#44CF6C', '#FF3939']}}
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Suprimentos e sangrias'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={earningsAndExpenses}
            options={{
              isStacked: false,
              colors: ['#44CF6C', '#FF3939'],
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Valor (R$)',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
